import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Contact.css';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    package: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');

  // Prefill package/interest from URL parameters (?paket=... or ?interesse=...)
  useEffect(() => {
    const paket = searchParams.get('paket');
    const interesse = searchParams.get('interesse');
    const prefill = paket || interesse;
    if (prefill) {
      setFormData((prev) => ({
        ...prev,
        package: prefill,
        message: prev.message || `Hallo Niels,\n\nich interessiere mich für: ${prefill}.\n\nBitte kontaktiere mich für weitere Details.`
      }));
      // Smooth scroll to form
      setTimeout(() => {
        const formEl = document.querySelector('.contact-form-wrapper');
        if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    // 1) Save inquiry to backend so it shows up in admin dashboard
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const axios = (await import('axios')).default;
      const res = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        package: formData.package,
        message: formData.message,
      });
      if (res.data?.confirmation_number) {
        setConfirmationNumber(res.data.confirmation_number);
      }
    } catch (err) {
      console.error('Saving inquiry failed (continuing to WhatsApp):', err);
    }

    // 2) Compose WhatsApp message
    const message = `Hallo Niels! 👋%0A%0A` +
      `Name: ${formData.name}%0A` +
      `E-Mail: ${formData.email}%0A` +
      (formData.phone ? `Telefon: ${formData.phone}%0A` : '') +
      (formData.package ? `Interessiert an: ${formData.package}%0A` : '') +
      `%0ANachricht:%0A${formData.message}`;

    window.open(`https://wa.me/4901604905213?text=${message}`, '_blank');
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-bg"></div>
        <div className="contact-hero-content">
          <span className="section-label">Kontakt</span>
          <h1 className="contact-hero-title">
            Lass uns <span className="gold">sprechen</span>
          </h1>
          <p className="contact-hero-subtitle">
            Bereit, deine Social Media Präsenz auf das nächste Level zu heben?<br />
            Ich freue mich auf deine Nachricht.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-section">
        <div className="contact-container">
          {/* Contact Info */}
          <div className="contact-info">
            <h2 className="contact-info-title">Mein Kontakt</h2>
            <p className="contact-info-subtitle">
              Wähle den Weg, der dir am liebsten ist. Ich melde mich schnellstmöglich bei dir zurück.
            </p>

            <div className="contact-methods">
              <a 
                href="https://wa.me/4901604905213" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-method"
              >
                <div className="method-icon">💬</div>
                <div className="method-info">
                  <span className="method-label">WhatsApp</span>
                  <span className="method-value">0160 4905213</span>
                </div>
                <span className="method-arrow">→</span>
              </a>

              <a 
                href="mailto:hoefner.digitalmarketing@gmail.com"
                className="contact-method"
              >
                <div className="method-icon">✉️</div>
                <div className="method-info">
                  <span className="method-label">E-Mail</span>
                  <span className="method-value">hoefner.digitalmarketing@gmail.com</span>
                </div>
                <span className="method-arrow">→</span>
              </a>

              <a 
                href="https://instagram.com/hoefner.agency" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-method"
              >
                <div className="method-icon">📷</div>
                <div className="method-info">
                  <span className="method-label">Instagram</span>
                  <span className="method-value">@hoefner.agency</span>
                </div>
                <span className="method-arrow">→</span>
              </a>
            </div>

            <div className="contact-quote">
              <p>"Dein Erfolg ist mein Antrieb.<br />Lass uns etwas Großes bewegen!"</p>
              <span className="quote-author">– Niels Höfner</span>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <h2 className="form-title">Schreibe mir eine Nachricht</h2>
            <p className="form-subtitle">
              Erzähl mir kurz von deinem Projekt – ich melde mich innerhalb von 24 Stunden.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Dein Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="deine@email.de"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Telefon (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="0123 456789"
                  />
                </div>

                <div className="form-group">
                  <label>Interesse</label>
                  <select
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    data-testid="contact-package-select"
                  >
                    <option value="">Wähle ein Thema</option>
                    <option value="Erstgespräch">Kostenloses Erstgespräch</option>
                    <option value="Starter Paket">Starter Paket (199€/Monat)</option>
                    <option value="Growth Paket">Growth Paket (249€/Monat)</option>
                    <option value="Pro Paket">Pro Paket (299€/Monat)</option>
                    <option value="Business Paket">Business Paket (349€/Monat)</option>
                    <option value="Premium Paket">Premium Paket (399€/Monat)</option>
                    <option value="E-Book Anfrage">E-Book "Social Media Guide" Anfrage</option>
                    <option value="Individuelles Angebot">Individuelles Angebot</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Deine Nachricht *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Erzähl mir kurz von deinem Projekt und deinen Zielen..."
                  ></textarea>
                </div>

                <button type="submit" className="form-submit" disabled={submitting} data-testid="contact-submit-button">
                  {submitting ? 'Wird gesendet...' : 'Nachricht über WhatsApp senden'}
                  <span className="btn-arrow">→</span>
                </button>

                <p className="form-disclaimer">
                  Deine Nachricht wird sicher und direkt an mein WhatsApp übertragen.
                </p>
              </form>
            ) : (
              <div className="form-success" data-testid="contact-success">
                <div className="success-icon">✓</div>
                <h3>Vielen Dank für deine Anfrage! 🤝</h3>
                <p>
                  Ich werde mich schnellstmöglich bei Ihnen melden 📲
                </p>

                {confirmationNumber && (
                  <div
                    data-testid="confirmation-number"
                    style={{
                      margin: '24px auto 8px',
                      padding: '18px 20px',
                      background: 'rgba(212, 175, 55, 0.08)',
                      border: '1px solid rgba(212, 175, 55, 0.35)',
                      borderRadius: 14,
                      maxWidth: 360,
                      textAlign: 'center',
                    }}
                  >
                    <div style={{
                      fontSize: 11,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: '#D4AF37',
                      marginBottom: 6,
                      fontWeight: 600,
                    }}>
                      Deine Bestätigungsnummer
                    </div>
                    <div style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: 'monospace',
                      letterSpacing: 1,
                    }}>
                      {confirmationNumber}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.55)',
                      marginTop: 8,
                    }}>
                      Bitte gib diese Nummer an, falls du dich später noch einmal meldest.
                    </div>
                  </div>
                )}

                <button 
                  className="form-submit" 
                  onClick={() => {
                    setSubmitted(false);
                    setConfirmationNumber('');
                    setFormData({ name: '', email: '', phone: '', package: '', message: '' });
                  }}
                >
                  Neue Nachricht
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
